import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { 
  LayoutDashboard, FileQuestion, ClipboardList, BarChart3, 
  Shield, LogOut, Users, Plus, Search, Edit, Trash2, Activity, MessageSquare
} from "lucide-react";

const QuestionBank = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [filterRole, setFilterRole] = useState("all");
  const [filterDifficulty, setFilterDifficulty] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  
  // Form state
  const [formData, setFormData] = useState({
    question_text: "",
    options: ["", "", "", ""],
    correct_answer: 0,
    difficulty: "medium",
    topic: "",
    role: "BA",
    explanation: ""
  });

  useEffect(() => {
    fetchQuestions();
  }, [filterRole, filterDifficulty]);

  const fetchQuestions = async () => {
    try {
      let url = `${API}/questions?`;
      if (filterRole !== "all") url += `role=${filterRole}&`;
      if (filterDifficulty !== "all") url += `difficulty=${filterDifficulty}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuestions(response.data);
    } catch (error) {
      toast.error("Failed to fetch questions");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingQuestion) {
        await axios.put(`${API}/questions/${editingQuestion.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Question updated successfully");
      } else {
        await axios.post(`${API}/questions`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Question created successfully");
      }
      setShowDialog(false);
      resetForm();
      fetchQuestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save question");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this question?")) return;
    
    try {
      await axios.delete(`${API}/questions/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Question deleted");
      fetchQuestions();
    } catch (error) {
      toast.error("Failed to delete question");
    }
  };

  const openEditDialog = (question) => {
    setEditingQuestion(question);
    setFormData({
      question_text: question.question_text,
      options: question.options,
      correct_answer: question.correct_answer,
      difficulty: question.difficulty,
      topic: question.topic,
      role: question.role,
      explanation: question.explanation || ""
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setEditingQuestion(null);
    setFormData({
      question_text: "",
      options: ["", "", "", ""],
      correct_answer: 0,
      difficulty: "medium",
      topic: "",
      role: "BA",
      explanation: ""
    });
  };

  const handleLogout = () => {
    logout();
    navigate("/auth");
  };

  const navItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/admin" },
    { icon: FileQuestion, label: "Question Bank", path: "/admin/questions", active: true },
    { icon: ClipboardList, label: "Exams", path: "/admin/exams" },
    { icon: BarChart3, label: "Results", path: "/admin/results" },
    { icon: Users, label: "Organisers", path: "/admin/organisers" },
    { icon: MessageSquare, label: "Feedback", path: "/admin/feedback" },
    { icon: Activity, label: "IP Logs", path: "/admin/logs" }
  ];

  const filteredQuestions = questions.filter(q => 
    q.question_text.toLowerCase().includes(searchQuery.toLowerCase()) ||
    q.topic.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getDifficultyBadge = (difficulty) => {
    const classes = {
      easy: "badge-easy",
      medium: "badge-medium",
      hard: "badge-hard"
    };
    return <span className={`px-2 py-1 rounded-md text-xs font-medium capitalize ${classes[difficulty]}`}>{difficulty}</span>;
  };

  return (
    <div className="flex min-h-screen bg-slate-50" data-testid="question-bank">
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-600 to-blue-500 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>edSolutions360</span>
              <p className="text-xs text-slate-500">Examination Portal</p>
            </div>
          </div>
          
          <nav className="space-y-2">
            {navItems.map((item) => (
              <div
                key={item.path}
                className={`nav-item ${item.active ? 'active' : ''}`}
                onClick={() => navigate(item.path)}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </div>
            ))}
          </nav>
        </div>
        
        <div className="mt-auto p-6 border-t border-slate-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-900">{user?.full_name}</p>
              <p className="text-xs text-slate-500">{user?.email}</p>
            </div>
          </div>
          <Button variant="outline" className="w-full" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Question Bank</h1>
              <p className="text-slate-600 mt-1">{questions.length} questions in database</p>
            </div>
            <Button 
              onClick={() => { resetForm(); setShowDialog(true); }}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="add-question-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Question
            </Button>
          </div>

          {/* Filters */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    placeholder="Search questions..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="search-questions-input"
                  />
                </div>
                <Select value={filterRole} onValueChange={setFilterRole}>
                  <SelectTrigger className="w-40" data-testid="filter-role-select">
                    <SelectValue placeholder="Role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="BA">Business Analyst</SelectItem>
                    <SelectItem value="MERN">MERN Developer</SelectItem>
                    <SelectItem value="Frontend">Frontend Developer</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterDifficulty} onValueChange={setFilterDifficulty}>
                  <SelectTrigger className="w-40" data-testid="filter-difficulty-select">
                    <SelectValue placeholder="Difficulty" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Levels</SelectItem>
                    <SelectItem value="easy">Easy</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="hard">Hard</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Questions List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner" />
            </div>
          ) : filteredQuestions.length === 0 ? (
            <Card className="text-center py-12">
              <FileQuestion className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h4 className="text-lg font-medium text-slate-900 mb-2">No Questions Found</h4>
              <p className="text-slate-500">Add questions or adjust your filters.</p>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredQuestions.map((question, index) => (
                <Card key={question.id} className="card-hover" data-testid={`question-card-${index}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <Badge variant="outline">{question.role === "BA" ? "Business Analyst" : question.role === "Frontend" ? "Frontend Developer" : "MERN Developer"}</Badge>
                          {getDifficultyBadge(question.difficulty)}
                          <span className="text-sm text-slate-500">Topic: {question.topic}</span>
                        </div>
                        <p className="text-slate-900 font-medium mb-3">{question.question_text}</p>
                        <div className="grid grid-cols-2 gap-2">
                          {question.options.map((option, idx) => (
                            <div 
                              key={idx} 
                              className={`text-sm p-2 rounded-lg ${
                                idx === question.correct_answer 
                                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                                  : 'bg-slate-50 text-slate-600'
                              }`}
                            >
                              <span className="font-medium mr-2">{String.fromCharCode(65 + idx)}.</span>
                              {option}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => openEditDialog(question)}
                          data-testid={`edit-question-${index}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDelete(question.id)}
                          data-testid={`delete-question-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingQuestion ? "Edit Question" : "Add New Question"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <Label>Role</Label>
                <Select value={formData.role} onValueChange={(v) => setFormData({...formData, role: v})}>
                  <SelectTrigger data-testid="question-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BA">Business Analyst</SelectItem>
                    <SelectItem value="MERN">MERN Developer</SelectItem>
                    <SelectItem value="Frontend">Frontend Developer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="form-group">
                <Label>Difficulty</Label>
                <Select value={formData.difficulty} onValueChange={(v) => setFormData({...formData, difficulty: v})}>
                  <SelectTrigger data-testid="question-difficulty-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="easy">Easy</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="hard">Hard</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="form-group">
              <Label>Topic</Label>
              <Input
                value={formData.topic}
                onChange={(e) => setFormData({...formData, topic: e.target.value})}
                placeholder="e.g., JavaScript, Requirements Gathering"
                data-testid="question-topic-input"
              />
            </div>
            
            <div className="form-group">
              <Label>Question</Label>
              <Textarea
                value={formData.question_text}
                onChange={(e) => setFormData({...formData, question_text: e.target.value})}
                placeholder="Enter the question text"
                rows={3}
                data-testid="question-text-input"
              />
            </div>
            
            <div className="form-group">
              <Label>Options</Label>
              <div className="space-y-2">
                {formData.options.map((option, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-sm font-medium">
                      {String.fromCharCode(65 + idx)}
                    </span>
                    <Input
                      value={option}
                      onChange={(e) => {
                        const newOptions = [...formData.options];
                        newOptions[idx] = e.target.value;
                        setFormData({...formData, options: newOptions});
                      }}
                      placeholder={`Option ${String.fromCharCode(65 + idx)}`}
                      data-testid={`question-option-${idx}-input`}
                    />
                    <input
                      type="radio"
                      name="correct"
                      checked={formData.correct_answer === idx}
                      onChange={() => setFormData({...formData, correct_answer: idx})}
                      className="w-4 h-4 text-indigo-600"
                      data-testid={`question-correct-${idx}-radio`}
                    />
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-1">Select the correct answer</p>
            </div>
            
            <div className="form-group">
              <Label>Explanation (Optional)</Label>
              <Textarea
                value={formData.explanation}
                onChange={(e) => setFormData({...formData, explanation: e.target.value})}
                placeholder="Explain the correct answer"
                rows={2}
                data-testid="question-explanation-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-question-btn">
              {editingQuestion ? "Update" : "Create"} Question
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuestionBank;
